import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Star, ShieldCheck, DoorOpen, Wallet } from "lucide-react";
import { MaskLine } from "../components/Reveal";
import { PillButton } from "../components/PillButton";
import { STATS } from "../data/content";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yPhone = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 6]);

  return (
    <section
      ref={ref}
      className="relative pt-32 md:pt-40 pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      {/* geometric backdrop */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-24 -left-20 w-72 h-72 rounded-full bg-brand-light" />
        <div className="absolute bottom-10 right-10 w-40 h-40 border-2 border-black rounded-3xl rotate-12" />
        <div
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage:
              "radial-gradient(#0033A2 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            maskImage: "radial-gradient(circle at 50% 30%, black, transparent 70%)",
            WebkitMaskImage: "radial-gradient(circle at 50% 30%, black, transparent 70%)",
          }}
        />
      </div>

      <div className="container-x">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="inline-flex items-center gap-2 bg-brand-light border-2 border-[#0033A2] text-[#0033A2] rounded-full px-4 py-1.5 font-heading font-bold text-sm mb-8"
          data-testid="hero-badge"
        >
          <Star size={15} fill="#0033A2" strokeWidth={0} />
          Trusted by 2,400+ societies across India
        </motion.div>

        <h1 className="font-heading font-black tracking-tighter leading-[0.92] text-[13vw] md:text-[8.2vw]">
          <MaskLine delay={0.15}>Run your society</MaskLine>
          <MaskLine delay={0.28}>
            <span className="text-[#0033A2]">like a dream.</span>
          </MaskLine>
        </h1>

        <div className="mt-8 grid md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="md:col-span-6 text-lg md:text-xl font-medium text-black/80 leading-relaxed max-w-xl"
          >
            grooh brings billing, visitor security, complaints, amenities and your entire
            community into one beautifully simple app. Less chaos, more community.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.72, duration: 0.6 }}
            className="md:col-span-6 flex flex-wrap gap-4 md:justify-end"
          >
            <PillButton to="/contact" testid="hero-cta-primary">
              Book a free demo <ArrowUpRight size={18} strokeWidth={2.5} />
            </PillButton>
            <PillButton to="/pricing" variant="ghost" testid="hero-cta-secondary">
              See pricing
            </PillButton>
          </motion.div>
        </div>

        {/* Product spotlight */}
        <div className="relative mt-16 md:mt-24">
          <motion.div
            style={{ y: yPhone }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-3xl overflow-hidden border-2 border-black shadow-hard"
            data-testid="hero-product-image"
          >
            <img
              src="https://images.unsplash.com/photo-1624204386084-dd8c05e32226?auto=format&fit=crop&w=1600&q=80"
              alt="Modern residential community"
              className="w-full h-[300px] md:h-[560px] object-cover"
            />
            {/* solid block overlay with app-like stat cards */}
            <div className="absolute inset-0 bg-[#0033A2]/10" />

            <motion.div
              style={{ rotate }}
              className="absolute left-4 bottom-4 md:left-8 md:bottom-8 bg-white border-2 border-black rounded-2xl p-4 md:p-5 shadow-hard-sm w-[210px] md:w-[260px]"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#0033A2] flex items-center justify-center">
                  <Wallet size={20} className="text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="font-heading font-black text-xl leading-none">96%</p>
                  <p className="text-xs font-semibold text-black/60">Dues collected</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              style={{ y: yBadge }}
              className="absolute right-4 top-4 md:right-8 md:top-8 bg-white border-2 border-black rounded-2xl p-4 md:p-5 shadow-hard-sm w-[200px] md:w-[240px]"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center">
                  <DoorOpen size={20} className="text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="font-heading font-black text-xl leading-none">Verified</p>
                  <p className="text-xs font-semibold text-black/60">Visitor at Gate 2</p>
                </div>
              </div>
            </motion.div>

            <div className="absolute right-4 bottom-4 md:right-8 md:bottom-8 bg-[#0033A2] text-white border-2 border-black rounded-2xl px-4 py-3 shadow-hard-sm hidden sm:flex items-center gap-2">
              <ShieldCheck size={18} strokeWidth={2.5} />
              <span className="font-heading font-bold text-sm">Bank-grade secure</span>
            </div>
          </motion.div>
        </div>

        {/* Stats bar */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 border-2 border-black rounded-3xl overflow-hidden" data-testid="hero-stats">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              className={`p-6 md:p-8 ${i % 2 === 0 ? "bg-white" : "bg-brand-light"} ${
                i < STATS.length - 1 ? "border-b-2 md:border-b-0 md:border-r-2 border-black" : ""
              } ${i === 0 || i === 2 ? "border-r-2 border-black md:border-r-2" : ""}`}
            >
              <p className="font-heading font-black text-3xl md:text-4xl text-[#0033A2] tracking-tight">
                {s.value}
              </p>
              <p className="mt-1 font-semibold text-black/60 text-sm">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
